import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, Download, LoaderCircle, Share2, X } from 'lucide-react';
import { toast } from 'sonner';
import SharePoster from './SharePoster';
import CompletionSharePoster from './CompletionSharePoster';
import { createCompletionPosterModel } from './completionPosterModel';
import { SHARE_ASSETS, type ShareAssetImages } from './shareAssets';
import { createPosterModel, type WorkoutShareCardData } from './workoutShareData';
import { downloadShareImage, exportSharePoster, loadCompletionQR, loadShareImages, sharePreparedImage } from './shareImageExport';
import './workout-share.css';

export default function WorkoutShareDialog({ data, onClose, variant = 'record' }: {
  data?: WorkoutShareCardData; onClose?: () => void; variant?: 'record' | 'completion';
}) {
  const model = useMemo(() => createPosterModel(data), [data]);
  const receipt = useMemo(() => variant === 'completion' && data ? createCompletionPosterModel(data) : null, [data, variant]);
  const fileName = receipt?.fileName || model.fileName;
  const svg = useRef<SVGSVGElement>(null);
  const trigger = useRef(document.activeElement);
  const [images, setImages] = useState<ShareAssetImages>(SHARE_ASSETS);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [prepared, setPrepared] = useState<{ file: File; url: string } | null>(null);
  const [sharing, setSharing] = useState(false);
  const shareLock = useRef(false);
  useEffect(() => {
    let stopped = false;
    setError(''); setReady(false);
    const request = receipt ? loadCompletionQR().then(qr => ({ ...SHARE_ASSETS, qr })) : loadShareImages();
    request.then(result => { if (!stopped) { setImages(result); setReady(true); } })
      .catch(() => { if (!stopped) setError('图片素材加载失败，请重试'); });
    return () => { stopped = true; };
  }, [retry, receipt]);
  useEffect(() => {
    let stopped = false;
    let objectUrl = '';
    setPrepared(null);
    if (ready && svg.current) exportSharePoster(svg.current).then(blob => {
      if (stopped) return;
      objectUrl = URL.createObjectURL(blob);
      setPrepared({ file: new File([blob], fileName, { type: 'image/png' }), url: objectUrl });
    }).catch(() => { if (!stopped) setError('分享图片生成失败，请重试'); });
    return () => {
      stopped = true;
      // Allow a started download to consume the URL even if the dialog closes immediately.
      if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    };
  }, [ready, model, receipt, fileName, retry]);

  const save = () => {
    if (!prepared) return;
    downloadShareImage(prepared.url, fileName);
    toast.success('已开始下载图片，可从浏览器下载中保存');
  };
  const share = async () => {
    if (!prepared || shareLock.current) return;
    shareLock.current = true; setSharing(true);
    try {
      const result = await sharePreparedImage(prepared.file);
      if (result === 'download') { downloadShareImage(prepared.url, fileName); toast.info('当前浏览器不支持图片分享，已开始下载图片'); }
    } finally { shareLock.current = false; setSharing(false); }
  };
  return <Dialog.Root open onOpenChange={open => { if (!open) onClose?.(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="share-overlay"/>
      <Dialog.Content className="share-dialog" aria-describedby={undefined} onCloseAutoFocus={event => {
        event.preventDefault();
        if (trigger.current instanceof HTMLElement && trigger.current.isConnected) trigger.current.focus();
      }}>
        <header className="share-header">
          <Dialog.Close asChild><button type="button" className="share-round" aria-label="返回"><ChevronLeft/></button></Dialog.Close>
          <Dialog.Title>{data ? '分享训练' : '分享练刻'}</Dialog.Title>
          <Dialog.Close asChild><button type="button" className="share-round" aria-label="关闭分享"><X/></button></Dialog.Close>
        </header>
        <div className="share-scroll">{receipt ? <CompletionSharePoster ref={svg} model={receipt} qr={images.qr}/>
          : <SharePoster ref={svg} model={model} images={images}/>}</div>
        <footer className="share-footer">
          {error ? <p className="share-status" role="alert">{error} <button type="button" onClick={() => setRetry(n => n + 1)}>重试</button></p>
            : !prepared && <p className="share-status" role="status"><LoaderCircle size={13} className="animate-spin"/>正在生成高清图片…</p>}
          <div className="share-actions">
            <button type="button" onClick={share} disabled={!prepared || sharing} className="share-action share-action-primary">
              {sharing ? <LoaderCircle className="animate-spin"/> : <Share2/>}<span>分享图片</span>
            </button>
            <button type="button" onClick={save} disabled={!prepared || sharing} className="share-action share-action-secondary"><Download/><span>保存图片</span></button>
          </div>
        </footer>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
